import { Modal } from 'antd';
import { useAppSelector } from 'hooks';
import { BillTypes, Product, ProductOption } from 'models';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getSelectedProductOptions, moneyFormat } from 'utils';
import * as svg from '../../../constants/ui/svg';

interface IModalSelectOptionProduct {
  open: boolean;
  setOpen: (open: boolean) => void;
  selectedProduct: Product | null;
  handleAddToCart: (product: Product, option: any) => void;
  setSelectedProduct: (product: Product) => void;
}

interface OptionProductItemProps {
  item: any;
  index: number;
  selectedShop: any;
  selectedOption: any[];
  setSelectedOption: (options: any[]) => void;
  selectedProduct: Product | null;
}

const OptionProductItem: FC<OptionProductItemProps> = ({
  item,
  index,
  selectedShop,
  selectedOption,
  setSelectedOption,
  selectedProduct,
}) => {
  const active = selectedOption?.find((i) => i.option_id === item.id);

  return (
    <div
      key={index}
      className="flex items-center justify-between h-12 border-b border-gray-200 py-3 cursor-pointer hover:bg-gray-50"
      onClick={() => {
        const updatedOptions = selectedOption.filter((option) => option.option_id !== item.id);
        if (updatedOptions.length === selectedOption.length) {
          updatedOptions.push({
            option_type_id: selectedProduct?.product_options?.[0]?.id,
            option_id: item.id,
            price: item.price,
          });
        }
        setSelectedOption(updatedOptions);
      }}
    >
      <div className="flex items-center gap-3 flex-1">
        <div
          className={`w-6 h-6 rounded-md border border-gray-300 flex items-center justify-center ${
            active ? 'bg-primary border-primary' : 'bg-white'
          }`}
        >
          {active && (
            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          )}
        </div>
        <div className="flex items-center justify-between flex-1">
          <span className="text-sm font-noto-sans-medium font-medium text-black truncate">
            {item?.name || ' - '}
          </span>
          <div>
            {item.value !== 'none' && (
              <span className="text-sm text-black">
                {moneyFormat(item?.price, selectedShop?.currency)}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const ModalSelectOptionProduct: FC<IModalSelectOptionProduct> = ({
  open,
  setOpen,
  selectedProduct,
  setSelectedProduct,
  handleAddToCart,
}) => {
  const { t } = useTranslation();

  const shopInfo: any = useAppSelector((state) => state.shop.shopInfo);
  const billInfo: BillTypes = useAppSelector((state) => state.bill.billInfo);

  const isRestaurant = shopInfo?.career?.name === 'RESTAURANT';

  const [selectedOption, setSelectedOption] = useState<ProductOption[]>([]);

  useEffect(() => {
    if (open) {
      setSelectedOption(selectedProduct?.selected_options || []);
    }
  }, [open, selectedProduct]);

  const totalPriceProduct = useMemo(() => {
    const optionPrice = getSelectedProductOptions(
      selectedProduct?.product_options || [],
      selectedOption,
      'price'
    );
    return (selectedProduct?.price + optionPrice) * (selectedProduct?.quantity || 0);
  }, [selectedProduct, selectedOption]);

  const listOptionByProduct = useMemo(() => {
    if (
      selectedProduct?.product_options &&
      Array.isArray(selectedProduct.product_options) &&
      selectedProduct.product_options.length > 0
    ) {
      const options = [...(selectedProduct.product_options[0]?.options || [])];
      return options.sort((a, b) => a.orderby - b.orderby);
    }
    return [];
  }, [selectedProduct]);

  const onIncrease = () => {
    const VAT = (billInfo?.tax || 0) + (billInfo?.consumption_tax || 0);

    if (
      (typeof selectedProduct?.quantity === 'number' &&
        typeof selectedProduct?.stock === 'number' &&
        selectedProduct.quantity < selectedProduct.stock) ||
      selectedProduct?.active_stock === 0
    ) {
      const updatedProduct = { ...selectedProduct };
      updatedProduct.quantity = (updatedProduct.quantity || 0) + 1;
      const optionPrice = getSelectedProductOptions(
        updatedProduct.product_options,
        updatedProduct.selected_options,
        'price'
      );
      updatedProduct.amount = (optionPrice + updatedProduct.price) * updatedProduct.quantity;
      if (isRestaurant) {
        updatedProduct.vat = VAT;
        updatedProduct.vat_money = (updatedProduct.amount * VAT) / 100;
        updatedProduct.price_no_vat = updatedProduct.amount - updatedProduct.vat_money;
      }
      updatedProduct.selected_options = selectedOption;

      setSelectedProduct(updatedProduct);
    } else {
    }
  };

  const onDecrease = () => {
    if (selectedProduct?.quantity === 1) {
      return;
    }
    const updatedProduct: Product = { ...selectedProduct } as Product;
    updatedProduct.quantity = (updatedProduct.quantity || 0) - 1;
    const optionPrice = getSelectedProductOptions(
      updatedProduct.product_options,
      updatedProduct.selected_options,
      'price'
    );
    updatedProduct.amount = (optionPrice + updatedProduct.price) * updatedProduct.quantity;
    if (isRestaurant) {
      const VAT = billInfo?.tax + billInfo?.consumption_tax;
      updatedProduct.vat = VAT;
      updatedProduct.vat_money = (updatedProduct.amount * VAT) / 100;
      updatedProduct.price_no_vat = updatedProduct.amount - updatedProduct.vat_money;
    }

    updatedProduct.selected_options = selectedOption;

    setSelectedProduct(updatedProduct);
  };

  return (
    <Modal
      open={open}
      onCancel={() => setOpen(false)}
      closeIcon={<></>}
      footer={null}
      centered
      width={'100%'}
      className="call-staff-modal"
    >
      <div>
        <div className="flex items-center justify-between border-b border-border_primary px-4 py-3">
          <span className="text-brown_dark text-xl font-noto-sans-bold font-bold">
            {t('OPTIONS')} ({moneyFormat(totalPriceProduct, shopInfo?.currency)})
          </span>
          <button
            onClick={() => setOpen(false)}
            className="text-primary font-noto-sans-medium font-medium"
          >
            {t('CLOSE')}
          </button>
        </div>

        <div className="flex flex-col gap-2 px-4 py-3">
          <div className="flex gap-x-2 items-center justify-between">
            <div className="flex flex-col gap-2">
              <span className="text-sm font-noto-sans-medium font-medium">
                {selectedProduct?.name}
              </span>
            </div>
            <div className="flex gap-x-2">
              <button
                className="bg-primary text-sm text-white w-[40px] h-[40px] rounded-full flex items-center justify-center"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onDecrease();
                }}
              >
                {svg.minusIcon}
              </button>
              <div className="w-[60px] h-[40px] rounded-lg flex items-center justify-center bg-white border border-solid border-[#E5E7EB]">
                {selectedProduct?.quantity}
              </div>
              <button
                className="bg-primary text-sm text-white w-[40px] h-[40px] rounded-full flex items-center justify-center"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onIncrease();
                }}
              >
                {svg.plusIcon}
              </button>
            </div>
          </div>
          <div className="flex flex-col">
            {listOptionByProduct.map((item, index) => (
              <OptionProductItem
                key={index}
                item={item}
                index={index}
                selectedShop={shopInfo}
                selectedOption={selectedOption}
                setSelectedOption={setSelectedOption}
                selectedProduct={selectedProduct}
              />
            ))}
          </div>
        </div>
        <div className="px-3 py-4 border-t border-#D0D5DD">
          <button
            onClick={() => {
              if (selectedProduct) {
                handleAddToCart(selectedProduct, selectedOption);
                setOpen(false);
              }
            }}
            className="w-full bg-primary text-white rounded-lg font-noto-sans-medium font-medium text-lg py-3"
            disabled={!selectedProduct}
          >
            {t('ORDER')}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ModalSelectOptionProduct;
